import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { Button } from "./ui";

const COOLDOWN_MS = 60_000;
const STORAGE_KEY = "gaole_verify_resend_until";

function secondsUntil(untilMs: number): number {
  return Math.max(0, Math.ceil((untilMs - Date.now()) / 1000));
}

export function ResendVerificationEmail() {
  const user = useAuth((s) => s.user);
  const [busy, setBusy] = useState(false);
  const [cooldownSec, setCooldownSec] = useState(0);

  useEffect(() => {
    const until = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (until > Date.now()) setCooldownSec(secondsUntil(until));
  }, []);

  useEffect(() => {
    if (cooldownSec <= 0) return;
    const timer = window.setInterval(() => {
      const until = Number(localStorage.getItem(STORAGE_KEY) || 0);
      const next = secondsUntil(until);
      setCooldownSec(next);
      if (next <= 0) localStorage.removeItem(STORAGE_KEY);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownSec]);

  if (!user || user.emailVerifiedAt) return null;

  async function resend() {
    setBusy(true);
    try {
      await api("/auth/resend-verification", { body: {} });
      const until = Date.now() + COOLDOWN_MS;
      localStorage.setItem(STORAGE_KEY, String(until));
      setCooldownSec(secondsUntil(until));
      toast("success", "Verification email sent — check your inbox");
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        localStorage.setItem(STORAGE_KEY, String(Date.now() + COOLDOWN_MS));
        setCooldownSec(secondsUntil(Date.now() + COOLDOWN_MS));
      }
      toast("error", err instanceof ApiError ? err.message : "Couldn't send verification email");
    } finally {
      setBusy(false);
    }
  }

  let label = "Resend verification email";
  if (busy) label = "Sending…";
  else if (cooldownSec > 0) label = `Resend in ${cooldownSec}s`;

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-2.5">
        <Mail size={16} className="mt-0.5 shrink-0 text-amber-400" />
        <div>
          <p className="text-xs font-semibold text-amber-200">Verify your email</p>
          <p className="mt-0.5 text-[11px] text-white/55">
            Check your inbox for the confirmation link, or resend it below.
          </p>
        </div>
      </div>
      <Button
        variant="outline"
        className="shrink-0 text-xs"
        disabled={busy || cooldownSec > 0}
        onClick={() => void resend()}
      >
        {label}
      </Button>
    </div>
  );
}
